import { Component, OnInit, OnDestroy, Output, EventEmitter, Input, ViewChild } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { ReplaySubject, takeUntil } from 'rxjs';

import {
  ModalColorPickerComponent,
  ModalDialogData,
} from 'app/modules/scootaverse/components/modal-color-picker/modal-color-picker.component';
import {
  ModalUploadImageComponent,
  UploadImage,
} from 'app/modules/scootaverse/components/modal-upload-image/modal-upload-image.component';
import { BackgroundService } from 'app/modules/social/services/background/background.service';
import { RoomSizeInfo } from 'app/modules/social/types/room';
import { SnackBarComponent, SnackBarData } from 'app/shared/snackbar/snackbar.component';
import { Loggable } from 'app/utils/logging/loggable';
import SocialTheme from 'entity/SocialTheme';
import SocialThemeData from 'entity/SocialThemeData';
import SocialThemeType from 'enums/SocialThemeType';

interface NewCategoryMap {
  themeCategory: string;
  themes: SocialTheme[];
  themesQnt: number;
}

@Component({
  selector: 'background-card',
  templateUrl: './background-card.component.html',
  styleUrls: ['./background-card.component.scss'],
  standalone: false,
})
export class BackgroundCardComponent extends Loggable implements OnInit, OnDestroy {
  private destroyed$ = new ReplaySubject<boolean>(1);
  protected newMappedCategory: NewCategoryMap[] = [];
  public showLoader = false;

  @Input() public hasBackStepButton = true;
  @Input() public roomSize?: RoomSizeInfo;
  @Input() public selectedBackground?: SocialTheme;
  @Output() public selectedBackgroundChange = new EventEmitter<SocialTheme>();
  @Output() public backStepClick = new EventEmitter<boolean>();
  @Output() public unSavedChanges = new EventEmitter<boolean>();
  @Input() public isCreateFlow = true;

  @ViewChild('tabGroup') public tabGroup!: MatTabGroup;

  public color = '#ffffff';
  protected colorSelected = false;
  protected selectedThemeId?: string;
  protected expandedRow!: number;
  protected selectedTheme?: SocialTheme;
  protected createViewPortSelected = false;
  protected createSingleColorSelected = false;
  protected createBackgroundSelected = false;
  protected uploadImageFlow = false;
  public backgroundName = new UntypedFormControl('', [Validators.required, Validators.maxLength(100)]);
  public uploadImage?: UploadImage;
  protected imageSelected = false;
  private createBackgroundTabSelected = false;
  protected uploadImageFar?: UploadImage;
  protected uploadImageMid?: UploadImage;
  protected uploadImageNear?: UploadImage;

  protected imageProgress: number | undefined;

  protected isEditing = false;
  private selectedSocialThemeId?: string;
  protected selectedTab = 0;

  public constructor(
    private backgroundService: BackgroundService,
    private dialog: MatDialog,
    private _snackBar: MatSnackBar,
  ) {
    super();
    this.setDisplayName('BackgroundCardComponent');
  }

  public ngOnInit() {
    void this.getThemes();
  }

  public async getThemes() {
    this.showLoader = true;
    this.newMappedCategory = [];

    if (!this.roomSize) {
      this.error('The RoomSize is required to get theme info current value:', this.roomSize);
      return;
    }

    const roomSize = Number(this.roomSize.size);
    await this.backgroundService.getThemesInfos(roomSize).then((themes) => {
      if (themes) {
        this.newMappedCategory.push({
          themeCategory: 'Abstract',
          themes: themes.layered ?? [],
          themesQnt: themes.layeredCount ?? 0,
        });
        this.newMappedCategory.push({
          themeCategory: 'Image backgrounds',
          themes: themes.singleImage ?? [],
          themesQnt: themes.singleImageCount ?? 0,
        });
        this.newMappedCategory.push({
          themeCategory: 'Single color',
          themes: themes.singleColor ?? [],
          themesQnt: themes.singleColorCount ?? 0,
        });
        this.newMappedCategory.push({
          themeCategory: 'My Backgrounds',
          themes: themes.custom ?? [],
          themesQnt: themes.customCount ?? 0,
        });

        this.showLoader = false;
      }
    });

    if (this.selectedBackground) {
      this.selectedTheme = this.selectedBackground;
      this.selectedThemeId = this.selectedBackground.socialThemeId;
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public async nextStep(): Promise<void> {
    // If the user select an existing background
    if (this.tabGroup.selectedIndex === 0) {
      this.selectedBackgroundChange.emit(this.selectedTheme);
      return;
    }

    if (!this.uploadImageFlow && !this.createSingleColorSelected) {
      // If the user choice to create a viewport or image background, but didn't complete the specific flow
      this.uploadImageFlow = true;
      return;
    }

    if (this.isEditing) {
      void this.editSocialTheme();
      return;
    }

    if (this.createSingleColorSelected) {
      const theme = {
        type: SocialThemeType.SINGLE_COLOR,
        colorBackground: this.color,
        socialThemeName: this.color,
      };

      await this.createBackgroundTheme({ theme });
      return;
    }

    // If the user complete the specific flow
    await this.handleCreateBackground();
  }

  public backStep(): void {
    this.backStepClick.emit(true);
  }

  public isBackgroundContiguous(theme?: SocialTheme): boolean {
    return !!theme?.minimapImageURI;
  }

  public roomSetBackgroundImage(theme?: SocialTheme): string | undefined {
    if (theme) {
      return theme.minimapImageURI;
    } else {
      return undefined;
    }
  }

  public backgroundHasImages(theme?: SocialTheme): boolean {
    return !!theme?.imageFarURI || !!theme?.imageMidURI || !!theme?.imageNearURI;
  }

  public roomImageFarURI(theme?: SocialTheme): string {
    return theme?.imageFarURI ? theme?.imageFarURI : '';
  }

  public roomImageMidURI(theme?: SocialTheme): string {
    return theme?.imageMidURI ? theme?.imageMidURI : '';
  }

  public roomImageNearURI(theme?: SocialTheme): string {
    return theme?.imageNearURI ? theme?.imageNearURI : '';
  }

  public openColorPickerDialog(): void {
    const data: ModalDialogData = {
      color: this.color,
    };

    this.dialog
      .open<ModalColorPickerComponent, ModalDialogData>(ModalColorPickerComponent, {
        data,
        width: '350px',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((color: string) => {
        if (color) {
          //If the user didn't select a color, the previous chose is keep
          this.color = color;
          this.colorSelected = true;
        }
      });
  }

  public expandRow(index: number): void {
    this.expandedRow = index === this.expandedRow ? -1 : index;
  }

  public selectTheme(theme: SocialTheme): void {
    this.selectedThemeId = theme.socialThemeId;
    this.selectedTheme = theme;
  }

  public selectSimpleColorTheme(): void {
    this.createSingleColorSelected = true;
    this.createViewPortSelected = false;
    this.createBackgroundSelected = false;
  }

  public selectViewPortTheme(): void {
    this.createSingleColorSelected = false;
    this.createViewPortSelected = true;
    this.createBackgroundSelected = false;
  }

  public selectBackgroundTheme(): void {
    this.createSingleColorSelected = false;
    this.createViewPortSelected = false;
    this.createBackgroundSelected = true;
  }

  public cancelCreation(): void {
    this.createSingleColorSelected = false;
    this.createViewPortSelected = false;
    this.createBackgroundSelected = false;
    this.uploadImageFlow = false;
    this.uploadImage = undefined;
    this.uploadImageFar = undefined;
    this.uploadImageMid = undefined;
    this.uploadImageNear = undefined;
    this.imageSelected = false;
    this.cancelEdit();
    this.unSavedChanges.emit(false);
  }

  private async handleCreateBackground(): Promise<void> {
    if (this.createViewPortSelected) {
      const themeViewport = {
        type: SocialThemeType.LAYERED,
        colorBackground: this.color,
        socialThemeName: this.backgroundName.value,
        addToMiniMap: true,
      };
      await this.createBackgroundTheme({
        theme: themeViewport,
        imageFarId: this.uploadImageFar?.imageId,
        imageMidId: this.uploadImageMid?.imageId,
        imageNearId: this.uploadImageNear?.imageId,
      });
    }
  }

  private async createBackgroundTheme(themeData: SocialThemeData): Promise<void> {
    await this.backgroundService
      .createCustomTheme(themeData)
      .then((theme) => {
        this.selectedBackgroundChange.emit(theme);
      })
      .catch((e) => {
        this.error('Error when creating background theme', e);
      });
  }

  public openUploadImageDialog(imageLayer: string): void {
    this.dialog
      .open<ModalUploadImageComponent>(ModalUploadImageComponent, {
        width: '635px',
        data: {
          roomSize: Number(this.roomSize?.size),
          useCropper: this.createBackgroundSelected,
          theme: {
            socialThemeId: this.selectedSocialThemeId,
            type: this.selectedSocialThemeType,
            colorBackground: this.color,
            socialThemeName: this.backgroundName.value,
            addToMiniMap: true,
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((uploadImage: UploadImage) => {
        if (!uploadImage) {
          return;
        }

        if (uploadImage.SocialThemeType === SocialThemeType.SINGLE_IMAGE) {
          this.selectedBackgroundChange.emit(uploadImage.theme);
        }

        if (uploadImage.SocialThemeType === SocialThemeType.LAYERED) {
          if (imageLayer === 'Far') {
            this.uploadImageFar = uploadImage;
          }
          if (imageLayer === 'Mid') {
            this.uploadImageMid = uploadImage;
          }
          if (imageLayer === 'Near') {
            this.uploadImageNear = uploadImage;
          }
        }
      });
  }

  public get disableNextBtn(): boolean {
    if (this.showLoader) {
      return true;
    }

    // background library teb selected and no theme selected
    if (!this.createBackgroundTabSelected && !this.selectedThemeId) {
      return true;
    }

    // background library teb selected image has not been changed
    if (!this.createBackgroundTabSelected) {
      return !!(this.selectedBackground && this.selectedThemeId === this.selectedBackground.socialThemeId);
    }

    if (this.hasSelectedThemeType && !this.uploadImageFlow) {
      return false;
    }

    if (this.backgroundName.valid && this.hasUploadedImage) {
      return false;
    }

    return true;
  }

  public selectedTabChange(tabChangeEvent: MatTabChangeEvent): void {
    this.createBackgroundTabSelected = tabChangeEvent.index === 1;

    if (tabChangeEvent.index === 0) {
      this.cancelEdit();
    }
  }

  public getThemeLabel(category: string, type?: SocialThemeType): string {
    if (category === 'My Backgrounds') {
      switch (type) {
        case SocialThemeType.LAYERED: {
          return 'Viewport Effects';
        }
        case SocialThemeType.SINGLE_COLOR: {
          return 'Single Color';
        }
        case SocialThemeType.SINGLE_IMAGE: {
          return 'Image';
        }
        default:
          return '';
      }
    }
    return '';
  }

  private cancelEdit() {
    if (this.isEditing) {
      this.color = '';
      this.colorSelected = false;
      this.selectedTab = 0;
      this.uploadImageFlow = false;
      this.isEditing = false;
    }
  }

  protected startEditCustomTheme(theme: SocialTheme) {
    if (theme.colorBackground) {
      this.color = theme.colorBackground;
      this.colorSelected = true;
    }

    if (theme.type === SocialThemeType.LAYERED) {
      this.uploadImageFar = { selectedImage: theme.imageFarURI };
      this.createViewPortSelected = true;
    }

    if (theme.type === SocialThemeType.SINGLE_IMAGE) {
      this.uploadImage = { selectedImage: theme.minimapImageURI };
      this.createBackgroundSelected = true;
    }

    this.createSingleColorSelected = theme.type === SocialThemeType.SINGLE_COLOR;
    this.backgroundName.setValue(theme.socialThemeName);
    this.createBackgroundTabSelected = true;
    this.selectedSocialThemeId = theme.socialThemeId;
    this.isEditing = true;
    this.selectedTab = 1;
  }

  private async editSocialTheme() {
    if (!this.selectedSocialThemeId) {
      this.error('Error while editing theme, selectedSocialThemeId is undefined');
      return;
    }

    const theme = {
      socialThemeId: this.selectedThemeId,
      type: SocialThemeType.SINGLE_COLOR,
      colorBackground: this.color,
      socialThemeName: this.color,
    };

    const themeData: SocialThemeData = {};

    if (this.createViewPortSelected) {
      theme.type = SocialThemeType.LAYERED;
      theme.socialThemeName = this.backgroundName.value;
      themeData.imageNearId = this.uploadImageNear?.imageId;
      themeData.imageMidId = this.uploadImageMid?.imageId;
      themeData.imageFarId = this.uploadImageFar?.imageId;
    }

    await this.backgroundService
      .updateSocialTheme({ ...themeData, theme })
      .then(() => {
        const _data: SnackBarData = {
          message: `Success! Your theme has been updated successfully.`,
          type: 'success',
          icon: 'APPROVE_ICON',
        };
        this._snackBar.openFromComponent(SnackBarComponent, {
          duration: 8000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: 'custom-snackbar',
          data: _data,
        });

        this.cancelEdit();
        void this.getThemes();
      })
      .catch((err) => this.error(err));
  }

  protected async deleteCustomTheme(socialThemeId?: string) {
    if (!socialThemeId) {
      this.error('Error while deleting the theme, socialThemeId is undefined');
      return;
    }
    await this.backgroundService
      .deleteCustomTheme(socialThemeId)
      .then(() => void this.getThemes())
      .catch(({ error }) => {
        if (error && error.usedCount) {
          const _data: SnackBarData = {
            message: `This theme is currently being used in ${error.usedCount} Scootaverse(s). Please change the theme in all instances before attempting to delete it.`,
            type: 'error',
            icon: 'WARNING',
          };
          this._snackBar.openFromComponent(SnackBarComponent, {
            duration: 8000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            panelClass: 'custom-snackbar',
            data: _data,
          });
          return;
        }
        this.error(error);
      });
  }

  private get hasUploadedImage() {
    if (
      this.uploadImage?.imageBytes ||
      (this.uploadImageFar?.imageId && this.uploadImageMid?.imageId && this.uploadImageFar.imageId)
    ) {
      return true;
    }
    return false;
  }

  private get hasSelectedThemeType() {
    return this.createSingleColorSelected || this.createViewPortSelected || this.createBackgroundSelected;
  }

  private get selectedSocialThemeType() {
    if (this.createBackgroundSelected) {
      return SocialThemeType.SINGLE_IMAGE;
    }
    if (this.createViewPortSelected) {
      return SocialThemeType.LAYERED;
    }

    return SocialThemeType.SINGLE_COLOR;
  }
}
